

import "bootstrap/dist/css/bootstrap.min.css";
import * as d3 from "d3";

import { useEffect, useRef } from "react";

/**
 * BarGraph React component that renders a D3.js bar chart.
 * @memberof module:components/graphs
 * @param {Object} props
 * @param {number} props.width - The width of the SVG container.
 * @param {number} props.height - The height of the SVG container.
 * @param {Object} props.dataset - The dataset and configuration for the chart.
 * @param {string} props.dataset.title - Title text displayed above the chart.
 * @param {Array<string>} props.dataset.legendValues - Array of legend label strings.
 * @param {Array<{x: string, y: number}>} props.dataset.chartValues - Data points for bars with x and y values.
 * @param {number} props.dataset.yMinValue - Minimum y-axis value.
 * @param {number} props.dataset.yMaxValue - Maximum y-axis value.
 * @param {number} props.dataset.yTicks - Number of ticks on the y-axis.
 * @param {string} props.dataset.xLabelName - Label text for the x-axis.
 * @param {string} props.dataset.yLabelName - Label text for the y-axis.
 * @param {number|Array<number>} props.dataset.thresholdValues - Threshold value(s) for bar coloring and threshold line(s).
 * @param {number} props.dataset.thresholdType - Type indicating color logic for thresholds (0 or 1).
 * @param {string} [props.dataset.warning] - Optional warning message displayed below the chart.
 *
 * @returns {JSX.Element} Bar graph SVG with legend, axes, bars, tooltip, and optional warning.
 */
const BarGraph = ({ width, height, dataset }) => {
  const ref = useRef();

  useEffect(() => {
    if (d3.select(ref.current)) d3.select(ref.current).selectAll("*").remove();
    draw();
  }, [dataset]);

  /**
   * Draws the bar chart using D3.js inside the SVG referenced by ref.
   * Handles axes, bars, legend, tooltip, threshold lines, and animations.
   */
  const draw = () => {
    const margin = 70;

    var svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height);

    svg
      .append("text")
      .attr("transform", "translate(100,0)")
      .attr("x", width / 4)
      .attr("y", 15)
      .attr("font-size", "15px")
      .attr("text-anchor", "middle")
      .attr("stroke", "white")
      .text(dataset.title);

    width = width - margin;
    height = height - margin;

    var legend = svg
      .append("g")
      .attr("class", "legend")
      .attr("transform", "translate(" + width + ", 0)");

    legend
      .selectAll("line")
      .data(dataset.legendValues)
      .enter()
      .append("line")
      .attr("x1", 20)
      .attr("y1", function (d, i) {
        return (i + 1) * 18;
      })
      .attr("x2", 50)
      .attr("y2", function (d, i) {
        return (i + 1) * 18;
      })
      .style("stroke", "white")
      .style("stroke-dasharray", "5,5");

    legend
      .selectAll("text")
      .data(dataset.legendValues)
      .enter()
      .append("text")
      .text(function (d) {
        return d;
      })
      .attr("x", 50)
      .attr("y", function (d, i) {
        return (i + 1) * 18 - 6;
      })
      .attr("text-anchor", "start")
      .attr("font-size", "10px")
      .attr("fill", "white")
      .attr("alignment-baseline", "hanging");

    var legendWidth = d3.select(".legend").node().getBoundingClientRect().width;

    d3.select(ref.current)
      .attr("width", width + margin + legendWidth)
      .attr("height", height + margin);

    //Axis
    var xScale = d3.scaleBand().range([0, width]).padding(0.4),
      yScale = d3.scaleLinear().range([height, 0]);

    var g = svg
      .append("g")
      .attr("transform", "translate(" + 35 + "," + 35 + ")");

    xScale.domain(
      dataset.chartValues.map(function (d) {
        return d.x;
      })
    );
    yScale.domain([dataset.yMinValue, dataset.yMaxValue]);

    var xAxis = g
      .append("g")
      .attr("class", "xAxis")
      .attr("transform", "translate(0," + height + ")")
      .call(d3.axisBottom(xScale))
      .append("text")
      .attr("y", 100 - margin)
      .attr("x", width / 2)
      .style("fill", "white")
      .attr("font-size", "12px")
      .attr("text-anchor", "middle")
      .text(dataset.xLabelName);

    var rotate = false;

    var yAxis = g
      .append("g")
      .attr("class", "yAxis")
      .call(
        d3
          .axisLeft(yScale)
          .tickFormat(function (d) {
            if (d.toString().length > 3) {
              rotate = true;
              return d.toExponential();
            } else return d;
          })
          .ticks(dataset.yTicks)
      );

    if (rotate)
      g.selectAll("g.yAxis g.tick")
        .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "0em")
        .attr("transform", "rotate(-65)");

    g.append("g")
      .attr("class", "yAxisLabel")
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", 30)
      .attr("dy", "-4.7em")
      .style("fill", "white")
      .attr("font-size", "12px")
      .attr("text-anchor", "middle")
      .text(dataset.yLabelName);

    //Grids
    g.selectAll("g.xAxis g.tick")
      .append("line")
      .attr("class", "gridline")
      .attr("x1", 0)
      .attr("y1", -height)
      .attr("x2", 0)
      .attr("y2", 0)
      .attr("stroke", "#4e4d4d")
      .attr("stroke-width", "0.5");

    g.selectAll("g.yAxis g.tick")
      .append("line")
      .attr("class", "gridline")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", width)
      .attr("y2", 0)
      .attr("stroke", "#4e4d4d")
      .attr("stroke-width", "0.5");

    // Tooltip
    var tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("font-family", "'Open Sans', sans-serif")
      .style("font-size", "12px")
      .style("z-index", "10")
      .style("background-color", "#A7CDFA")
      .style("color", "#B380BA")
      .style("border", "solid")
      .style("border-color", "#A89ED6")
      .style("padding", "2px")
      .style("border-radius", "2px")
      .style("visibility", "hidden");

    //Data
    g.selectAll(".bar")
      .data(dataset.chartValues)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .style("stroke-width", "2")
      .attr("stroke", function (d) {
        var alpha = 1;
        if (d.y < dataset.thresholdValues && dataset.thresholdType === 0)
          return `rgba(17, 125, 220, ${alpha})`;

        if (d.y >= dataset.thresholdValues && dataset.thresholdType === 0)
          return `rgba(255, 0, 0, ${alpha})`;

        if (d.y < dataset.thresholdValues && dataset.thresholdType === 1)
          return `rgba(255, 0, 0, ${alpha})`;

        if (d.y >= dataset.thresholdValues && dataset.thresholdType === 1)
          return `rgba(17, 125, 220, ${alpha})`;
      })
      .attr("x", function (d) {
        return xScale(d.x) - 2;
      })
      .attr("y", function (d) {
        return yScale(0);
      })
      .attr("width", xScale.bandwidth() + 2 * 2)
      .attr("fill", function (d) {
        var alpha = 0.75;
        if (d.y < dataset.thresholdValues && dataset.thresholdType === 0)
          return `rgba(17, 125, 220, ${alpha})`;

        if (d.y >= dataset.thresholdValues && dataset.thresholdType === 0)
          return `rgba(255, 0, 0, ${alpha})`;

        if (d.y < dataset.thresholdValues && dataset.thresholdType === 1)
          return `rgba(255, 0, 0, ${alpha})`;

        if (d.y >= dataset.thresholdValues && dataset.thresholdType === 1)
          return `rgba(17, 125, 220, ${alpha})`;
      })
      .attr("height", function (d) {
        var h = height - yScale(0);
        if (h > 0) return h;
      })

      .on("mouseover", function (d) {
        tooltip.style("visibility", "visible").text(d.x);
        d3.select(this).style("opacity", "0.7");
      })
      .on("mousemove", (event, d) => {
        tooltip
          .style("top", event.pageY - 10 + "px")
          .style("left", event.pageX + 10 + "px")
          .text(d.y);
      })
      .on("mouseout", function (d) {
        tooltip.style("visibility", "hidden");
        d3.select(this).style("opacity", "1");
      });

    // Threshold
    g.selectAll(".thresholdLine")
      .data(dataset.thresholdValues)
      .enter()
      .append("line")
      .style("stroke", "white")
      .style("stroke-dasharray", "5,5")
      .attr("x1", 0)
      .attr("y1", function (d) {
        return yScale(d);
      })
      .attr("x2", width)
      .attr("y2", function (d) {
        return yScale(d);
      });

    // Animation
    svg
      .selectAll("rect")
      .transition()
      .duration(800)
      .attr("y", function (d) {
        if (d.y > dataset.yMaxValue) return yScale(dataset.yMaxValue);
        else return yScale(d.y);
      })
      .attr("height", function (d) {
        var h;
        if (d.y > dataset.yMaxValue) h = height - yScale(dataset.yMaxValue);
        else h = height - yScale(d.y);

        if (h > 0) return h;
      })
      .delay(function (d, i) {
        return i * 100;
      });
  };

  return (
    <>
      <div className="bar-graph" id="container">
        <svg className="bar-graph" ref={ref}></svg>
        {dataset.warning ? (
          <p>
            <span className="text-warning">
              <b>Warning:</b>
            </span>{" "}
            {dataset.warning}
          </p>
        ) : (
          <></>
        )}
        {/* <p><span class="text-warning"><b>Warning:</b></span>The quality of assessment of GLONASS performance is due to the unavailability of reliable reference products. {dataset.warning}</p> */}
      </div>
    </>
  );
};

export default BarGraph;
